import { useCallback, useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * Scroll restoration for every scrollable surface the user can leave and come
 * back to (mail list, reader-to-list, orders views, search, sidebar …).
 *
 * The position is remembered as an ANCHOR, not a pixel offset: the first row
 * that is still (partly) visible at the top of the viewport, identified by its
 * `data-scroll-key`, plus that row's measured distance from the viewport's top
 * edge, plus a handful of neighbouring keys as fallbacks for when the anchor
 * row itself disappears (archived, deleted, filtered out). Everything is
 * measured from the live DOM at capture and at restore time, so the mechanism
 * is indifferent to row heights, styling, sticky headers, virtualization or
 * lazy-loaded content. There is no height constant anywhere in this file.
 *
 * Capture is continuous (rAF-throttled on scroll) rather than "on the way out"
 * because by the time a React cleanup runs the DOM has typically already been
 * swapped, hidden or detached and every measurement reads zero.
 *
 * Restore is asynchronous and self-correcting: it waits for the anchor row to
 * exist (mutation observer + `ready` flag), positions it, then re-measures for
 * a few frames so late layout shifts (row measurement, images, fonts) can't
 * leave the list a few pixels off. Any user scroll intent cancels a pending
 * restore so the page never yanks the list out from under the user.
 */

export const SCROLL_KEY_ATTR = 'data-scroll-key';
/** Marks a sticky header inside a scroll viewport so a seeded "jump to row"
 *  anchor can land BELOW it instead of underneath it. */
export const SCROLL_STICKY_ATTR = 'data-scroll-sticky';

export interface ScrollAnchor {
  /** `data-scroll-key` of the anchor row; null when the viewport has no keyed rows. */
  key: string | null;
  /** Anchor row's top edge relative to the viewport's top edge, in px (≤ 0 when
   *  the row is partly scrolled past, > 0 when content above it is showing). */
  offset: number;
  /** Fallback keys, following rows first then preceding rows. */
  neighbors: string[];
  /** Raw pixel offset, the fallback of last resort. */
  scrollTop: number;
  /** When true the anchor is a seeded "jump here" target: the row is aligned
   *  just below whichever sticky headers are pinned over it. */
  belowSticky?: boolean;
  savedAt: number;
}

export type ApplyResult =
  /** Anchor row found and positioned. */
  | 'exact'
  /** Anchor row gone; a neighbouring row positioned in its place. */
  | 'neighbor'
  /** Anchor (or neighbour) row found but it can't sit at its offset yet: the
   *  content below it is too short and the viewport is pinned at its end. */
  | 'clamped'
  /** No keyed row found; raw scrollTop applied and the content was tall enough. */
  | 'pixel'
  /** No keyed row found; raw scrollTop applied but clamped (content still short). */
  | 'pixel-short'
  /** Nothing to restore to yet. */
  | 'none';

const MAX_ANCHORS = 80;
const NEIGHBOR_COUNT = 6;
/** How long a pending restore keeps waiting for its anchor row to appear
 *  after the surface reports ready (the best available fallback has already
 *  been applied by then; this is the window in which a late-rendering row can
 *  still pull the list to the exact spot). */
const RESTORE_DEADLINE_MS = 2000;
/** While the surface still reports `ready: false` the deadline re-arms, up to
 *  this many times, so a slow fetch doesn't get a half-restored list. */
const RESTORE_DEADLINE_REARMS = 15;
/** Frames of post-positioning re-measurement. Exits early once two consecutive
 *  frames needed no correction. */
const SETTLE_MAX_FRAMES = 12;
const SETTLE_STABLE_FRAMES = 2;
const SETTLE_TOLERANCE_PX = 0.5;

/** Flip `window.__SCROLL_RESTORE_DEBUG = true` in a console to trace. */
function debug(...args: unknown[]) {
  if ((window as unknown as { __SCROLL_RESTORE_DEBUG?: boolean }).__SCROLL_RESTORE_DEBUG) {
    console.debug('[ScrollRestore]', ...args);
  }
}

// ---------------------------------------------------------------------------
// Anchor store (session-scoped, LRU)
// ---------------------------------------------------------------------------

const anchors = new Map<string, ScrollAnchor>();

export function getScrollAnchor(scope: string): ScrollAnchor | null {
  const anchor = anchors.get(scope);
  if (!anchor) return null;
  // Refresh recency.
  anchors.delete(scope);
  anchors.set(scope, anchor);
  return anchor;
}

export function setScrollAnchor(scope: string, anchor: ScrollAnchor): void {
  anchors.delete(scope);
  anchors.set(scope, anchor);
  while (anchors.size > MAX_ANCHORS) {
    const oldest = anchors.keys().next().value;
    if (oldest === undefined) break;
    anchors.delete(oldest);
  }
}

export function clearScrollAnchor(scope: string): void {
  anchors.delete(scope);
}

/** Seed a scope with a "land on this row" anchor before navigating to it. The
 *  scope's restoration will wait for the row to render and align it below any
 *  pinned sticky header. */
export function seedScrollAnchor(scope: string, key: string): void {
  setScrollAnchor(scope, {
    key,
    offset: 0,
    neighbors: [],
    scrollTop: 0,
    belowSticky: true,
    savedAt: Date.now(),
  });
}

// ---------------------------------------------------------------------------
// DOM measurement
// ---------------------------------------------------------------------------

function keyOf(el: Element): string | null {
  return el.getAttribute(SCROLL_KEY_ATTR);
}

function isSticky(el: HTMLElement): boolean {
  return el.hasAttribute(SCROLL_STICKY_ATTR) || getComputedStyle(el).position === 'sticky';
}

function findByKey(viewport: HTMLElement, key: string): HTMLElement | null {
  return viewport.querySelector<HTMLElement>(`[${SCROLL_KEY_ATTR}="${CSS.escape(key)}"]`);
}

/**
 * Read the current anchor from the DOM: the first keyed row whose bottom edge
 * is below the viewport's top edge, in document order. Rows must be rendered
 * in visual order (true for plain lists and for the virtualized list, which
 * renders its window in index order).
 */
export function captureDomAnchor(viewport: HTMLElement): ScrollAnchor {
  const scrollTop = viewport.scrollTop;
  const savedAt = Date.now();
  const vpTop = viewport.getBoundingClientRect().top;
  const rows = viewport.querySelectorAll<HTMLElement>(`[${SCROLL_KEY_ATTR}]`);

  let anchorIndex = -1;
  for (let i = 0; i < rows.length; i += 1) {
    const rect = rows[i].getBoundingClientRect();
    if (rect.height <= 0) continue; // collapsed / not laid out
    if (rect.bottom <= vpTop + SETTLE_TOLERANCE_PX) continue; // scrolled past
    // A pinned sticky header sits at the viewport's top edge for the whole
    // length of its group, so its position says nothing about how far the
    // group is scrolled. Anchor to the first real row beneath it instead.
    if (isSticky(rows[i])) continue;
    anchorIndex = i;
    break;
  }

  if (anchorIndex < 0) {
    return { key: null, offset: 0, neighbors: [], scrollTop, savedAt };
  }

  const anchorEl = rows[anchorIndex];
  const key = keyOf(anchorEl);
  const offset = anchorEl.getBoundingClientRect().top - vpTop;

  const neighbors: string[] = [];
  for (let i = anchorIndex + 1; i < rows.length && neighbors.length < NEIGHBOR_COUNT; i += 1) {
    const k = keyOf(rows[i]);
    if (k && k !== key && !isSticky(rows[i])) neighbors.push(k);
  }
  const followingCount = neighbors.length;
  for (
    let i = anchorIndex - 1;
    i >= 0 && neighbors.length < followingCount + NEIGHBOR_COUNT;
    i -= 1
  ) {
    const k = keyOf(rows[i]);
    if (k && k !== key && !isSticky(rows[i])) neighbors.push(k);
  }

  return { key, offset, neighbors, scrollTop, savedAt };
}

/** Total height of sticky headers currently pinned over the viewport's top
 *  edge that overlap the given element. Measured, never assumed. */
function pinnedStickyOverlap(viewport: HTMLElement, target: HTMLElement): number {
  const vpTop = viewport.getBoundingClientRect().top;
  const targetTop = target.getBoundingClientRect().top;
  let overlap = 0;
  const stickies = viewport.querySelectorAll<HTMLElement>(`[${SCROLL_STICKY_ATTR}]`);
  stickies.forEach((sticky) => {
    if (sticky === target || sticky.contains(target)) return;
    const rect = sticky.getBoundingClientRect();
    if (rect.height <= 0) return;
    // Pinned at (or above) the viewport top and hanging over the target's top edge.
    if (rect.top <= vpTop + SETTLE_TOLERANCE_PX && rect.bottom > targetTop + SETTLE_TOLERANCE_PX) {
      overlap = Math.max(overlap, rect.bottom - targetTop);
    }
  });
  return overlap;
}

/** Scroll `viewport` so that `el` sits `offset` px below the viewport's top
 *  edge. Returns the correction that was applied (0 = already in place). */
function alignElement(viewport: HTMLElement, el: HTMLElement, offset: number): number {
  const vpTop = viewport.getBoundingClientRect().top;
  const delta = el.getBoundingClientRect().top - vpTop - offset;
  if (Math.abs(delta) <= SETTLE_TOLERANCE_PX) return 0;
  viewport.scrollTop += delta;
  return delta;
}

/**
 * Position the viewport on `anchor` using whatever is currently rendered.
 * Idempotent: calling it again once the layout is stable applies no change.
 */
export function applyDomAnchor(viewport: HTMLElement, anchor: ScrollAnchor): ApplyResult {
  const candidates = anchor.key ? [anchor.key, ...anchor.neighbors] : [];
  for (const candidate of candidates) {
    const el = findByKey(viewport, candidate);
    if (!el || el.getBoundingClientRect().height <= 0) continue;
    alignElement(viewport, el, anchor.offset);
    if (anchor.belowSticky) {
      const overlap = pinnedStickyOverlap(viewport, el);
      if (overlap > SETTLE_TOLERANCE_PX) {
        viewport.scrollTop -= overlap;
      }
      return candidate === anchor.key ? 'exact' : 'neighbor';
    }
    // Did it land? If the row is still BELOW where it belongs while the
    // viewport is already scrolled to its end, there isn't enough content
    // under the row to scroll it up any further — a partially loaded list,
    // not a wrong anchor.
    const residual = el.getBoundingClientRect().top - viewport.getBoundingClientRect().top - anchor.offset;
    const maxScroll = viewport.scrollHeight - viewport.clientHeight;
    if (residual > SETTLE_TOLERANCE_PX && viewport.scrollTop >= maxScroll - SETTLE_TOLERANCE_PX) {
      return 'clamped';
    }
    return candidate === anchor.key ? 'exact' : 'neighbor';
  }

  // No keyed row to align to (rows gone, or the surface has no keyed rows):
  // fall back to the raw offset. A null-key anchor at 0 is a deliberate
  // "top" — apply it too, so a shared container never inherits the previous
  // content's offset.
  const maxScroll = viewport.scrollHeight - viewport.clientHeight;
  if (Math.abs(viewport.scrollTop - anchor.scrollTop) > SETTLE_TOLERANCE_PX) {
    viewport.scrollTop = anchor.scrollTop;
  }
  if (!anchor.key && anchor.scrollTop === 0) return 'none';
  return maxScroll + SETTLE_TOLERANCE_PX >= anchor.scrollTop ? 'pixel' : 'pixel-short';
}

/** Bring a keyed row to the top of its viewport (below any pinned sticky
 *  header). For one-off jumps on an already-rendered list. */
export function scrollViewportToKey(viewport: HTMLElement, key: string): boolean {
  const el = findByKey(viewport, key);
  if (!el) return false;
  alignElement(viewport, el, 0);
  const overlap = pinnedStickyOverlap(viewport, el);
  if (overlap > SETTLE_TOLERANCE_PX) viewport.scrollTop -= overlap;
  return true;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export type LocateResult =
  /** The anchor (or a neighbour) exists in the data and has been brought into
   *  the rendered window; the DOM will have it shortly. */
  | 'located'
  /** None of the anchor's keys exist in the data any more. */
  | 'missing'
  /** Can't tell yet (data still loading). */
  | 'pending';

export interface ScrollRestorationOptions {
  /** Identity of the content in the viewport. A change means "different list":
   *  the old scope keeps its anchor, the new scope restores its own (or starts
   *  at the top). `null` disables the hook. Must change in the SAME render as
   *  the content it describes. */
  scopeKey: string | null;
  viewportRef: RefObject<HTMLElement | null>;
  /** Whether the viewport is currently the visible surface. A false→true
   *  transition re-runs restoration (the browser drops the scroll offset of a
   *  `display:none` subtree; a `visibility:hidden` one keeps it, in which case
   *  the restore is a cheap verification). */
  active?: boolean;
  /** Content readiness. While false, restoration waits instead of concluding
   *  the anchor row is gone. Defaults to true; surfaces that fetch on mount
   *  should pass their loading state. */
  ready?: boolean;
  /**
   * Virtualized lists only: the anchor row may exist in the data without being
   * in the DOM. Called when the DOM lookup fails; should scroll the viewport
   * near the row (using the virtualizer's estimates) so it renders, and report
   * whether it exists at all.
   */
  locate?: (anchor: ScrollAnchor) => LocateResult;
}

export interface ScrollRestorationHandle {
  /** Capture the current position immediately (e.g. right before a transition
   *  the hook can't observe). */
  flush: () => void;
  /** Abandon any in-flight restore (the user is taking over). */
  cancel: () => void;
}

export function useScrollRestoration({
  scopeKey,
  viewportRef,
  active = true,
  ready = true,
  locate,
}: ScrollRestorationOptions): ScrollRestorationHandle {
  // Scope whose listeners/restoration are currently wired up. Set from
  // effects (not render) so a deactivation cleanup that runs in the same
  // commit as a scope change still captures under the OLD scope.
  const liveScopeRef = useRef<string | null>(null);
  const restoringRef = useRef(false);
  const pendingRef = useRef<ScrollAnchor | null>(null);
  const captureRafRef = useRef(0);
  const attemptRafRef = useRef(0);
  const settleRafRef = useRef(0);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<MutationObserver | null>(null);
  const listenersRef = useRef<{ el: HTMLElement; detach: () => void } | null>(null);
  const locateRef = useRef(locate);
  locateRef.current = locate;
  const readyRef = useRef(ready);
  readyRef.current = ready;

  const capture = useCallback(() => {
    const viewport = viewportRef.current;
    const scope = liveScopeRef.current;
    if (!viewport || !scope || restoringRef.current) {
      debug('capture skipped', scope, { restoring: restoringRef.current, hasViewport: Boolean(viewport) });
      return;
    }
    // Hidden or detached subtrees measure as zero — keep the last good anchor.
    if (!viewport.isConnected || viewport.clientHeight === 0) {
      debug('capture skipped (not laid out)', scope, { connected: viewport.isConnected });
      return;
    }
    const anchor = captureDomAnchor(viewport);
    setScrollAnchor(scope, anchor);
    debug('capture', scope, anchor.key, Math.round(anchor.offset));
  }, [viewportRef]);

  const stopWaiting = useCallback(() => {
    if (attemptRafRef.current) {
      cancelAnimationFrame(attemptRafRef.current);
      attemptRafRef.current = 0;
    }
    if (settleRafRef.current) {
      cancelAnimationFrame(settleRafRef.current);
      settleRafRef.current = 0;
    }
    if (deadlineRef.current) {
      clearTimeout(deadlineRef.current);
      deadlineRef.current = null;
    }
    observerRef.current?.disconnect();
    observerRef.current = null;
  }, []);

  const finish = useCallback(
    (refresh: boolean) => {
      stopWaiting();
      const wasRestoring = restoringRef.current;
      restoringRef.current = false;
      pendingRef.current = null;
      if (wasRestoring && refresh) {
        // Store what actually ended up on screen so a neighbour/pixel
        // fallback doesn't have to be re-resolved on the next visit.
        capture();
      }
    },
    [capture, stopWaiting],
  );

  const cancel = useCallback(() => {
    if (restoringRef.current) {
      debug('cancel', liveScopeRef.current);
      finish(false);
    }
  }, [finish]);

  /** Re-measure for a few frames after positioning. While the surface isn't
   *  `ready` yet the restore stays pending afterwards: later DOM mutations and
   *  the readiness flip re-apply the anchor, holding the row in place as the
   *  fresh content lands. */
  const settle = useCallback(
    (viewport: HTMLElement, anchor: ScrollAnchor) => {
      if (settleRafRef.current) return; // a loop is already running
      let frames = 0;
      let stable = 0;
      const tick = () => {
        settleRafRef.current = 0;
        if (!restoringRef.current || pendingRef.current !== anchor) return;
        frames += 1;
        const before = viewport.scrollTop;
        const result = applyDomAnchor(viewport, anchor);
        const moved = Math.abs(viewport.scrollTop - before) > SETTLE_TOLERANCE_PX;
        stable = moved || result === 'clamped' ? 0 : stable + 1;
        if (stable >= SETTLE_STABLE_FRAMES || frames >= SETTLE_MAX_FRAMES) {
          debug('settled', liveScopeRef.current, { frames, ready: readyRef.current, scrollTop: viewport.scrollTop });
          if (readyRef.current) finish(true);
          return;
        }
        settleRafRef.current = requestAnimationFrame(tick);
      };
      settleRafRef.current = requestAnimationFrame(tick);
    },
    [finish],
  );

  const scheduleAttemptRef = useRef<() => void>(() => {});

  const attempt = useCallback(() => {
    const viewport = viewportRef.current;
    const anchor = pendingRef.current;
    if (!viewport || !anchor || !restoringRef.current) return;
    if (!viewport.isConnected || viewport.clientHeight === 0) return;

    const result = applyDomAnchor(viewport, anchor);
    debug('attempt', liveScopeRef.current, result);
    if (result === 'exact' || result === 'neighbor' || result === 'pixel') {
      settle(viewport, anchor);
      return;
    }
    if (result === 'none') {
      finish(false);
      return;
    }
    // Keyed anchor not in the DOM, or found but pinned at the end of content
    // that is still too short, or pixel fallback still clamped.
    if (anchor.key && locateRef.current) {
      if (!readyRef.current) return; // data still loading — wait
      const located = locateRef.current(anchor);
      if (result === 'clamped' && located !== 'pending') {
        // The list has nothing more to give — this is as close as it gets.
        settle(viewport, anchor);
        return;
      }
      if (located === 'missing') {
        viewport.scrollTop = 0;
        finish(false);
        return;
      }
      // 'located': the row renders on the virtualizer's next commit — the
      // observer re-attempts, plus one scheduled attempt in case the rendered
      // window didn't change. 'pending': wait for data.
      if (located === 'located') scheduleAttemptRef.current();
      return;
    }
    // Keyed-but-absent without a locator, or content still too short: keep
    // waiting for content (observer + deadline).
  }, [finish, settle, viewportRef]);

  const scheduleAttempt = useCallback(() => {
    if (attemptRafRef.current || !restoringRef.current) return;
    attemptRafRef.current = requestAnimationFrame(() => {
      attemptRafRef.current = 0;
      attempt();
    });
  }, [attempt]);
  scheduleAttemptRef.current = scheduleAttempt;

  const beginRestore = useCallback(
    (viewport: HTMLElement, anchor: ScrollAnchor) => {
      stopWaiting();
      pendingRef.current = anchor;
      restoringRef.current = true;
      debug('begin', liveScopeRef.current, anchor.key, Math.round(anchor.offset));

      const observer = new MutationObserver(() => scheduleAttempt());
      observer.observe(viewport, { childList: true, subtree: true });
      observerRef.current = observer;

      let rearms = 0;
      const armDeadline = () => {
        deadlineRef.current = setTimeout(() => {
          deadlineRef.current = null;
          if (!restoringRef.current || pendingRef.current !== anchor) return;
          if (!readyRef.current && rearms < RESTORE_DEADLINE_REARMS) {
            rearms += 1;
            armDeadline();
            return;
          }
          const vp = viewportRef.current;
          if (vp) applyDomAnchor(vp, anchor);
          debug('deadline', liveScopeRef.current);
          finish(true);
        }, RESTORE_DEADLINE_MS);
      };
      armDeadline();

      attempt();
    },
    [attempt, finish, scheduleAttempt, stopWaiting, viewportRef],
  );

  const ensureListeners = useCallback(
    (viewport: HTMLElement) => {
      if (listenersRef.current?.el === viewport) return;
      listenersRef.current?.detach();

      const onScroll = () => {
        if (restoringRef.current) {
          debug('scroll ignored (restoring)', liveScopeRef.current);
          return;
        }
        if (captureRafRef.current) {
          debug('scroll coalesced', liveScopeRef.current, captureRafRef.current);
          return;
        }
        captureRafRef.current = requestAnimationFrame(() => {
          captureRafRef.current = 0;
          capture();
        });
      };
      // Any user intent to scroll takes precedence over a pending restore.
      const onIntent = () => cancel();

      viewport.addEventListener('scroll', onScroll, { passive: true });
      viewport.addEventListener('wheel', onIntent, { passive: true });
      viewport.addEventListener('touchstart', onIntent, { passive: true });
      viewport.addEventListener('pointerdown', onIntent, { passive: true });
      viewport.addEventListener('keydown', onIntent);
      listenersRef.current = {
        el: viewport,
        detach: () => {
          viewport.removeEventListener('scroll', onScroll);
          viewport.removeEventListener('wheel', onIntent);
          viewport.removeEventListener('touchstart', onIntent);
          viewport.removeEventListener('pointerdown', onIntent);
          viewport.removeEventListener('keydown', onIntent);
          if (captureRafRef.current) {
            cancelAnimationFrame(captureRafRef.current);
            captureRafRef.current = 0;
          }
        },
      };
    },
    [cancel, capture],
  );

  // Deactivation / unmount: capture while the subtree is still laid out.
  // Declared BEFORE the restore effect so that, when `active` and `scopeKey`
  // flip in the same commit, this cleanup still sees the previous scope.
  useLayoutEffect(() => {
    if (!active) return;
    return () => {
      debug('deactivate/unmount capture', liveScopeRef.current);
      capture();
    };
  }, [active, capture]);

  // Activation / scope change / viewport (re)mount: restore, or reset to the
  // top for a scope with no anchor. Runs after every commit and compares
  // against what's currently wired, so a viewport that only mounts once its
  // page has loaded (loading placeholder → list) is picked up the moment it
  // exists, without the consumer having to signal it.
  const wiredRef = useRef<{ el: HTMLElement | null; scope: string | null }>({ el: null, scope: null });
  useLayoutEffect(() => {
    const el = viewportRef.current;
    const scope = active && scopeKey ? scopeKey : null;
    const wired = wiredRef.current;
    if (wired.el === el && wired.scope === scope) return;

    finish(false);
    wiredRef.current = { el, scope };
    liveScopeRef.current = scope;
    if (!el || !scope) return;
    ensureListeners(el);

    const anchor = getScrollAnchor(scope);
    if (!anchor) {
      // Fresh scope: never inherit the previous content's offset.
      if (el.scrollTop !== 0) el.scrollTop = 0;
      debug('fresh', scope);
      return;
    }
    beginRestore(el, anchor);
  });

  // Readiness flip while a restore is pending → try again.
  useEffect(() => {
    if (ready && restoringRef.current) scheduleAttempt();
  }, [ready, scheduleAttempt]);

  // Unmount: tear down listeners and timers. Also forget what was wired so a
  // re-run of the effects (React StrictMode's simulated remount in dev) wires
  // the viewport again instead of seeing "already wired" with no listeners.
  useEffect(() => {
    return () => {
      stopWaiting();
      listenersRef.current?.detach();
      listenersRef.current = null;
      wiredRef.current = { el: null, scope: null };
      liveScopeRef.current = null;
      restoringRef.current = false;
      pendingRef.current = null;
    };
  }, [stopWaiting]);

  return { flush: capture, cancel };
}
